import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
    ArrowRight,
    ArrowUpRight,
    Facebook,
    Instagram,
    Linkedin,
    Mail,
    MapPin,
    MessageCircle,
    Phone,
} from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import api, { trackClick } from "@/lib/api";
import { useContact } from "@/lib/contact";

const EASE = [0.22, 1, 0.36, 1];

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.65, ease: EASE, delay },
});

const EXPLORE = [
    { label: "Home", target: "top" },
    { label: "Workspaces", target: "#gallery" },
    { label: "Amenities", target: "#amenities" },
    { label: "Pricing", target: "#pricing" },
    { label: "Contact", target: "#location" },
];

const WORKSPACES = ["Coworking Area", "Private Cabin", "Premium Cabin", "Meeting Room"];

// Social icons render only once real profile URLs are configured
const SOCIALS = [
    { icon: Instagram, label: "Instagram", key: "instagram" },
    { icon: Linkedin, label: "LinkedIn", key: "linkedin" },
    { icon: Facebook, label: "Facebook", key: "facebook" },
];

const ColumnHeading = ({ children }) => (
    <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-ink/50">
        <span className="h-1 w-1 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" aria-hidden="true" />
        {children}
    </p>
);

const FooterLink = ({ label, onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="group flex items-center gap-1.5 text-left text-sm font-medium text-ink/65 transition-colors duration-300 hover:text-ink"
    >
        <span className="transition-transform duration-300 group-hover:translate-x-0.5">{label}</span>
        <ArrowUpRight
            size={13}
            strokeWidth={2.4}
            aria-hidden="true"
            className="text-brand-teal opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100"
        />
    </button>
);

export default function Footer() {
    const contact = useContact();
    const [socials, setSocials] = useState({});

    useEffect(() => {
        api.get("/api/content/social")
            .then((res) => setSocials(res.data || {}))
            .catch(() => {});
    }, []);

    const handleExplore = (link) => {
        if (link.target === "top") return scrollToTop();
        scrollToId(link.target);
    };

    return (
        <footer data-testid="site-footer" className="relative border-t border-slate-200/80 bg-white pt-px text-ink/70">
            {/* architectural backdrop — blueprint grid + restrained brand glows */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                <svg className="absolute inset-0 h-full w-full" fill="none">
                    <defs>
                        <pattern id="footerGrid" width="48" height="48" patternUnits="userSpaceOnUse">
                            <path d="M48 0H0V48" stroke="#16324F" strokeOpacity="0.045" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#footerGrid)" />
                </svg>
                <div
                    className="absolute -top-32 left-[8%] h-[420px] w-[420px] rounded-full opacity-[0.06]"
                    style={{ background: "radial-gradient(circle, #7E35A8 0%, transparent 65%)" }}
                />
                <div
                    className="absolute bottom-0 right-[4%] h-[380px] w-[380px] rounded-full opacity-[0.06]"
                    style={{ background: "radial-gradient(circle, #22B3A3 0%, transparent 65%)" }}
                />
            </div>

            <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
                {/* MAIN FOOTER */}
                <div className="grid grid-cols-1 gap-12 py-16 md:grid-cols-2 lg:grid-cols-12 lg:gap-8 lg:py-20">
                    {/* Brand */}
                    <motion.div {...fadeUp(0.05)} className="lg:col-span-4">
                        <button
                            data-testid="footer-logo"
                            onClick={scrollToTop}
                            aria-label="Work Island — back to top"
                            className="inline-block"
                        >
                            <img src="/assets/logo-full.png" alt="Work Island — Co-Work · Connect · Create" className="h-16 w-auto" />
                        </button>
                        <p className="mt-5 max-w-xs text-sm font-medium leading-relaxed text-ink/60">
                            A professional coworking space in Bikaner designed for focused work,
                            collaboration and growth.
                        </p>
                        <div className="mt-6 flex items-center gap-3">
                            {SOCIALS.map((social) => {
                                const href = socials && social ? socials[social.key] : null;
                                if (!href) return null;
                                const Icon = social.icon;
                                return (
                                    <a
                                        key={social.label}
                                        data-testid={`footer-social-${social.label.toLowerCase()}`}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Work Island on ${social.label}`}
                                        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-ink/60 transition-all duration-300 hover:scale-105 hover:border-brand-teal/50 hover:text-ink"
                                    >
                                        <Icon size={15} strokeWidth={2} />
                                    </a>
                                );
                            })}
                        </div>
                    </motion.div>

                    {/* Explore */}
                    <motion.nav {...fadeUp(0.12)} aria-label="Footer" className="lg:col-span-2">
                        <ColumnHeading>Explore</ColumnHeading>
                        <ul className="mt-5 space-y-3">
                            {EXPLORE.map((link) => (
                                <li key={link.label}>
                                    <FooterLink
                                        label={link.label}
                                        testId={`footer-link-${link.label.toLowerCase()}`}
                                        onClick={() => handleExplore(link)}
                                    />
                                </li>
                            ))}
                        </ul>
                    </motion.nav>

                    {/* Workspaces */}
                    <motion.nav {...fadeUp(0.18)} aria-label="Workspaces" className="lg:col-span-2">
                        <ColumnHeading>Workspaces</ColumnHeading>
                        <ul className="mt-5 space-y-3">
                            {WORKSPACES.map((space) => (
                                <li key={space}>
                                    <FooterLink
                                        label={space}
                                        testId={`footer-workspace-${space.toLowerCase().replace(/\s+/g, "-")}`}
                                        onClick={() => scrollToId("#pricing")}
                                    />
                                </li>
                            ))}
                        </ul>
                    </motion.nav>

                    {/* Contact */}
                    <motion.div {...fadeUp(0.24)} className="lg:col-span-4">
                        <ColumnHeading>Get in Touch</ColumnHeading>
                        <ul className="mt-5 space-y-4">
                            <li className="flex items-start gap-3">
                                <MapPin size={16} strokeWidth={2.2} className="mt-0.5 shrink-0 text-brand-teal" aria-hidden="true" />
                                <address className="text-sm font-medium not-italic leading-relaxed text-ink/65">
                                    {contact.name}, {contact.address.includes(", Near") ? `${contact.address.split(", Near")[0]},` : contact.address}
                                    {contact.address.includes(", Near") && (
                                        <>
                                            <br />
                                            Near{contact.address.split(", Near")[1]}
                                        </>
                                    )}
                                </address>
                            </li>
                            <li>
                                <a
                                    data-testid="footer-phone-link"
                                    href={contact.telUrl}
                                    onClick={() => trackClick("call", "footer")}
                                    className="group flex items-center gap-3 text-sm font-semibold text-ink/70 transition-colors hover:text-ink"
                                >
                                    <Phone size={16} strokeWidth={2.2} className="shrink-0 text-brand-blue transition-transform duration-300 group-hover:-translate-y-0.5" aria-hidden="true" />
                                    {contact.phoneDisplay}
                                </a>
                            </li>
                            <li>
                                <a
                                    data-testid="footer-email-link"
                                    href={contact.mailtoUrl}
                                    className="group flex items-center gap-3 text-sm font-semibold text-ink/70 transition-colors hover:text-ink"
                                >
                                    <Mail size={16} strokeWidth={2.2} className="shrink-0 text-brand-purple transition-transform duration-300 group-hover:-translate-y-0.5" aria-hidden="true" />
                                    {contact.email}
                                </a>
                            </li>
                        </ul>

                        <div className="mt-7 border-t border-slate-200 pt-6">
                            <p className="text-sm font-bold text-ink">
                                Have questions about our workspaces?
                            </p>
                            <div className="mt-4 flex flex-wrap items-center gap-3">
                                <button
                                    data-testid="footer-cta-book-demo"
                                    onClick={() => scrollToId("#book-demo")}
                                    className="btn-gradient group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold text-white"
                                >
                                    Book a Free Demo
                                    <ArrowRight size={14} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                                </button>
                                <a
                                    data-testid="footer-whatsapp-btn"
                                    href={contact.waLink("Hi Work Island, I have a question about your workspaces!")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackClick("whatsapp", "footer")}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-[13px] font-bold text-ink/70 transition-all duration-300 hover:border-brand-teal/60 hover:text-ink"
                                >
                                    <MessageCircle size={14} strokeWidth={2.4} aria-hidden="true" />
                                    WhatsApp Us
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* BOTTOM BAR */}
            <div data-testid="footer-bottom-bar" className="relative border-t border-slate-200">
                <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-6 pb-28 sm:flex-row sm:px-8 md:pb-6">
                    <p className="text-xs font-medium text-ink/50">
                        © 2026 {contact.name}. All Rights Reserved.
                    </p>
                    <div className="flex items-center gap-6 text-xs font-medium text-ink/60">
                        <span data-testid="footer-privacy-link">Privacy Policy</span>
                        <span data-testid="footer-terms-link">Terms &amp; Conditions</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
